import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { readContextRefs } from '@core/services/gbe-error-parser/warning-normalizer';

const isSiteLandmark = (entry: unknown): boolean => {
  const { relation, entity } = (entry ?? {}) as {
    relation?: unknown;
    entity?: { entity_type?: unknown };
  };
  return relation === 'LANDMARK' && entity?.entity_type === 'SITE';
};

/** Only an unvalidated route keeps a site out of the file; any other warning only informs. */
export const isSiteBlocker = (warning: CodedStatusMessage): boolean =>
  warning.errorCode === GbeErrorCodes.DEPLOY_ROUTE_NOT_VALIDATED;

/**
 * Splits a view's warnings into those about one site and everything else.
 *
 * A single-file channel (GAEL) has no message to list a warning under, so the back names the
 * warehouse as a `LANDMARK` SITE reference beside the culprit route. The site table shows
 * those warnings on the site's own row; the landmark is dropped from them there, since a chip
 * naming the row it sits in says nothing.
 */
export function splitSiteWarnings(warnings: CodedStatusMessage[]): {
  bySite: ReadonlyMap<string, CodedStatusMessage[]>;
  rest: CodedStatusMessage[];
} {
  const bySite = new Map<string, CodedStatusMessage[]>();
  const rest: CodedStatusMessage[] = [];
  for (const warning of warnings) {
    const siteUid = readContextRefs(warning.details)?.find(
      (ref) => ref.relation === 'LANDMARK' && ref.entityType === 'SITE'
    )?.uid;
    if (typeof siteUid !== 'string') {
      rest.push(warning);
      continue;
    }
    const context = (warning.details as Record<string, unknown>)['context'] as unknown[];
    const withoutLandmark: CodedStatusMessage = {
      ...warning,
      details: {
        ...warning.details,
        context: context.filter((entry) => !isSiteLandmark(entry))
      }
    };
    const siteWarnings = bySite.get(siteUid);
    if (siteWarnings === undefined) bySite.set(siteUid, [withoutLandmark]);
    else siteWarnings.push(withoutLandmark);
  }
  return { bySite, rest };
}
